import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { AddRolloutDraftsResponse } from '../../../../../redux/api/fymAPI.types';
import { StudioActionError } from '../../studio/StudioActionError';
import { StudioActionFeedback } from '../../studio/studioActionErrorText';
import { planMessages } from './planMessages';
import { planDayTitle } from './rolloutDayCopy';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Plan tab; each part uses its own slice of it */
import styles from './Plan.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AddDraftsResultProps {
  result: AddRolloutDraftsResponse | null;
  error: StudioActionFeedback | null;
}

/**
 * The line under "Add N drafts" (owner rule 2026-10-06: the result of an action sits next to its
 * button): the drafts made, the assets that became Studio jobs, the days with no asset, or the
 * error of the call.
 */
const AddDraftsResultComponent: React.FC<AddDraftsResultProps> = ({ result, error }) => {
  const intl = useIntl();
  const lines = useMemo(() => {
    if (result === null) {
      return [];
    }
    const queued = result.queued?.length ?? 0;
    const missing = result.skipped
      .filter((skipped) => skipped.reason === 'assetMissing')
      .map((skipped) => planDayTitle(intl, { key: skipped.key, asset: null }));
    const out: string[] = [];
    if (result.created > 0) {
      out.push(intl.formatMessage(planMessages.resultCreated, { count: result.created }));
    }
    if (queued > 0) {
      out.push(intl.formatMessage(planMessages.resultQueued, { count: queued }));
    }
    if (missing.length > 0) {
      out.push(
        intl.formatMessage(planMessages.resultMissing, { days: intl.formatList(missing, { type: 'conjunction' }) })
      );
    }
    if (out.length === 0) {
      out.push(intl.formatMessage(planMessages.resultNothing));
    }
    return out;
  }, [intl, result]);

  if (error !== null) {
    return <StudioActionError feedback={error} />;
  }
  if (lines.length === 0) {
    return null;
  }
  return (
    <div className={styles.result} role="status" data-testid="plan-add-result">
      {lines.map((line) => (
        <span key={line}>{line}</span>
      ))}
    </div>
  );
};

export const AddDraftsResult = TypedMemo(AddDraftsResultComponent);
